import {
  trackMusicModeToggled,
  trackMusicSplitterSelected,
} from '@src/analytics/EventTracker';
import ProgressBar from '@src/components/common/ProgressBar';
import { useEstimatedProgress } from '@src/components/common/useEstimatedProgress';
import { estimateStemSeparationMs } from '@src/config/processingEstimates';
import React from 'react';
import styled, { keyframes } from 'styled-components';

import { MusicActionsMenu } from './MusicActionsMenu';
import {
  type MusicMode,
  type MusicSeparationController,
  type SplitterChoice,
} from './useMusicSeparationController';

/**
 * The "Music and Sounds" control (PLAT-10622 + PLAT-10626, DE5/DE7). Sits
 * directly under the Original↔Dubbed voice control in the Transcript & Speakers
 * language dropdown, as a **single, constant-width line**: a label on the left
 * and a state-specific control + kebab on the right.
 *
 *  - **never separated** → a **Separate** action (PLAT-10622). Separation is
 *    capped to short tracks (`maxMinutes`, PLAT-10626); over the cap it's
 *    disabled with a note.
 *  - **separating** → a yellow pulsating **Separating Audio** state
 *    (PLAT-10665), matching the app's "Publishing" busy affordance.
 *  - **separated, music present** → a **None | From Original** toggle (inline);
 *    **From Original** plays the kept bed under the dub (only when the voice is
 *    Dubbed — otherwise disabled with a hint).
 *  - **separated, silent bed** → **No music found**.
 *  - once separated, a **kebab (⋮)** hosts **Re-separate** + the track
 *    **downloads** (`MusicActionsMenu`, PLAT-10627).
 *
 * Constant width: the wrapper has a `min-width` sized to the widest state and
 * notes wrap, so the surrounding `max-content` dropdown never jumps as the state
 * changes. Presentational + controlled — the parent owns the controller (so the
 * toggle and live-audio playback share one `musicMode` / `bedUrl`, DS6).
 */
interface MusicSeparationControlProps {
  controller: MusicSeparationController;
  /** Story id, for analytics (PLAT-10712). */
  storyId?: string;
  /** Story title, for the download filenames (PLAT-10715). */
  storyName?: string | null;
  /** Show the launch-period splitter picker (`enableStemSplitterPicker`, DE4). */
  showSplitterPicker?: boolean;
  /** The voice axis is Dubbed — the only mode the kept bed plays under (DS5). */
  voiceIsDubbed?: boolean;
  /** Composed AI dub track (for the download menu); `null` when none/prepared. */
  dubBuffer?: AudioBuffer | null;
  /** BCP-47 code of the dub language, for the download filename. */
  dubLanguage?: string | null;
  /** Source track duration (seconds); separation is capped at `maxMinutes`. */
  durationSec?: number | null;
  /**
   * Cap (minutes) on the track length separation is offered for
   * (LaunchDarkly-controlled, `stemSeparationMaxMinutes`; default 20). PLAT-10626.
   */
  maxMinutes?: number;
  /**
   * How much of this control to show (PLAT-10628).
   *
   * `full` (the default) is the story tab's whole panel, unchanged.
   *
   * `toggleOnly` is the clip preview's line: the two-position **None | From
   * Original** choice and nothing else. PLAT-10628 asks for exactly that — a
   * two-position control gated on Dubbed plus stems plus a non-silent bed. The
   * Separate action, the "Separating Audio" state, the "No music found" line and
   * the kebab of re-separate and downloads are the design's additions on top of
   * the ticket, and they are out of this delivery by Denys's decision of
   * 2026-08-19. They are also all story-wide, minutes-long operations that do not
   * belong inside one clip's screen. A clip with no bed to choose from shows
   * nothing at all rather than one of those states.
   *
   * Both variants paint the SAME two buttons from the same definition below, so
   * the labels, the levels and the pressed state cannot drift apart.
   */
  variant?: 'full' | 'toggleOnly';
  /**
   * react-tooltip ids for the hover hints (PLAT-11563): `tooltipId` for the
   * one-line hints on the None / From Original segments and the kebab,
   * `helpTooltipId` for the sentence-long explanation on Separate. The host
   * renders the <Tooltip> elements (style + z-index are its call); omitted ⇒ no
   * hints.
   */
  tooltipId?: string;
  helpTooltipId?: string;
}

const MUSIC_MODE_HINTS: Record<MusicMode, string> = {
  none: 'No music under the dub',
  original: "Keep the original recording's music under the dub",
};

/**
 * PLAT-11592 — why the choice is inert while the voice is not Dubbed. A bed can
 * only play where the picture's own sound is down; anywhere else the story's
 * music would be heard twice. Shown on hover of the greyed segments instead of
 * as a line of its own, so the language menu header does not grow by a row.
 */
const VOICE_NOT_DUBBED_HINT =
  'Switch voice to Dubbed to play the original music.';

const SPLITTERS: { value: SplitterChoice; label: string }[] = [
  { value: 'elevenlabs-isolation-v1', label: 'ElevenLabs' },
  { value: 'lalal-v1', label: 'lalal.ai' },
];

const MUSIC_MODES: { value: MusicMode; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'original', label: 'From Original' },
];

export const MusicSeparationControl: React.FC<MusicSeparationControlProps> = ({
  controller,
  storyId,
  storyName = null,
  showSplitterPicker = false,
  voiceIsDubbed = false,
  dubBuffer = null,
  dubLanguage = null,
  durationSec = null,
  maxMinutes = 20,
  variant = 'full',
  tooltipId,
  helpTooltipId,
}) => {
  const {
    status,
    hasStems,
    noMusic,
    bedUrlMp3,
    bedUrlWav,
    voiceUrlMp3,
    voiceUrlWav,
    originalUrl,
    provider,
    sizes,
    splitter,
    setSplitter,
    musicMode,
    setMusicMode,
    separate,
    reseparate,
    cancel,
    runStartedAt,
  } = controller;

  const running = status === 'running';
  // Estimated progress while the splitter runs (it reports no percentage) —
  // the same idiom as the dub modal's footer, tuned by the source length. The
  // start time comes from the controller so closing and reopening the language
  // menu (which unmounts this row) doesn't rewind the estimate to 0%.
  const progress = useEstimatedProgress(
    running,
    estimateStemSeparationMs({ videoDurationSec: durationSec }),
    { startedAt: runStartedAt },
  );
  const hasBed = hasStems && !noMusic;

  /** The two-position choice itself — the only part both variants share. */
  const modeSegments = (
    <div
      className="segments"
      role="group"
      aria-label="Music: none or from original"
    >
      {MUSIC_MODES.map(opt => (
        <button
          key={opt.value}
          type="button"
          className={musicMode === opt.value ? 'active' : ''}
          aria-pressed={musicMode === opt.value}
          // Inert, not disabled, so the reason can still show on hover.
          aria-disabled={voiceIsDubbed ? undefined : true}
          data-tooltip-id={tooltipId}
          data-tooltip-content={
            tooltipId
              ? voiceIsDubbed
                ? MUSIC_MODE_HINTS[opt.value]
                : VOICE_NOT_DUBBED_HINT
              : undefined
          }
          onClick={() => {
            if (!voiceIsDubbed) return;
            if (opt.value !== musicMode) {
              trackMusicModeToggled({
                story_id: storyId,
                mode: opt.value,
                language: dubLanguage ?? '',
              });
            }
            setMusicMode(opt.value);
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  // The clip preview's line. See `variant` above for what is left out and why.
  if (variant === 'toggleOnly') {
    if (!hasBed) return null;
    return (
      <Wrapper role="group" aria-label="Music and Sounds">
        <div className="row">
          <span className="label">Music and Sounds</span>
          <div className="controls">{modeSegments}</div>
        </div>
      </Wrapper>
    );
  }
  // Separation is capped to short tracks (cost/latency); the cap is
  // LaunchDarkly-controlled (`stemSeparationMaxMinutes`, default 20).
  const tooLong = durationSec != null && durationSec > maxMinutes * 60;

  return (
    <Wrapper role="group" aria-label="Music and Sounds">
      <div className="row">
        <span className="label">Music and Sounds</span>
        <div className="controls">
          {running ? (
            <span className="progress" role="status" aria-live="polite">
              <span className="separating">
                <span className="pulse" aria-hidden="true" />
                Separating Audio
                <span className="pct">{Math.round(progress)}%</span>
              </span>
              <ProgressBar value={progress} max={100} height={4} width={96} />
              <button
                type="button"
                className="cancel"
                data-tooltip-id={tooltipId}
                data-tooltip-content={
                  tooltipId
                    ? 'Stop waiting. The separation may still finish in the background.'
                    : undefined
                }
                onClick={cancel}
              >
                Cancel
              </button>
            </span>
          ) : !hasStems ? (
            <button
              type="button"
              className="action"
              disabled={tooLong || status === 'loading'}
              data-tooltip-id={helpTooltipId}
              data-tooltip-content={
                helpTooltipId
                  ? 'Split the original audio into voice and music, so the music can keep playing under the dubbed voice.'
                  : undefined
              }
              onClick={separate}
            >
              Separate
            </button>
          ) : noMusic ? (
            <span className="working">No music found</span>
          ) : (
            modeSegments
          )}
          {hasStems && (
            <MusicActionsMenu
              tooltipId={tooltipId}
              storyId={storyId}
              storyName={storyName}
              provider={provider}
              onReseparate={reseparate}
              reseparateDisabled={running || tooLong}
              voiceUrlMp3={voiceUrlMp3}
              voiceUrlWav={voiceUrlWav}
              bedUrlMp3={bedUrlMp3}
              bedUrlWav={bedUrlWav}
              originalUrl={originalUrl}
              sizes={sizes}
              dubBuffer={dubBuffer}
              dubLanguage={dubLanguage}
            />
          )}
        </div>
      </div>

      {showSplitterPicker && (
        <div className="row pickerRow">
          <span className="label">Splitter</span>
          <div className="segments" role="group" aria-label="Splitter">
            {SPLITTERS.map(opt => (
              <button
                key={opt.label}
                type="button"
                className={splitter === opt.value ? 'active' : ''}
                aria-pressed={splitter === opt.value}
                disabled={running}
                onClick={() => {
                  if (opt.value !== splitter) {
                    trackMusicSplitterSelected({
                      story_id: storyId,
                      provider: opt.value ?? 'default',
                    });
                  }
                  setSplitter(opt.value);
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {tooLong && (
        <p className="note">
          Music separation is available for tracks {maxMinutes} minutes or less.
        </p>
      )}
      {status === 'error' && (
        <p className="note error">Separation failed — try again.</p>
      )}
    </Wrapper>
  );
};

// Mirror publishStatusUi's "Publishing" busy pulse (yellow --accent dot fading
// 1 → 0.3 → 1 over 1.4s) for the in-progress "Separating Sound and Music" state.
const pulseDot = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

const Wrapper = styled.div`
  /* Constant width sized to the widest state (the "Original" hint line), so the
     max-content dropdown never jumps as the control's state changes. */
  min-width: 340px;
  min-height: 34px !important;
  box-sizing: border-box;
  padding: 2px 8px 8px;
  margin-bottom: 4px;
  border-bottom: 1px solid var(--border);

  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
  .pickerRow {
    margin-top: 6px;
  }
  .label {
    font-size: var(--text-xs);
    font-weight: var(--font-weight-semibold);
    color: var(--text-muted);
  }
  .controls {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .working {
    font-size: var(--text-xs);
    color: var(--text-muted);
  }
  .separating {
    display: inline-flex;
    align-items: center;
    gap: var(--spacing-xs);
    font-size: var(--text-xs);
    font-weight: var(--font-weight-medium);
    /* Gold (--accent) busy affordance, matching "Publishing". */
    color: var(--accent);
  }
  .progress {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  .separating .pct {
    color: var(--text-muted);
    font-variant-numeric: tabular-nums;
  }
  .cancel {
    border: none;
    background: transparent;
    padding: 2px 4px;
    font-size: var(--text-xs);
    font-weight: var(--font-weight-medium);
    color: var(--text-secondary);
    cursor: pointer;
    border-radius: 4px;
    transition:
      background-color 120ms ease,
      color 120ms ease;
  }
  .cancel:hover {
    background: var(--surface-elevated);
    color: var(--foreground);
  }
  .separating .pulse {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    animation: ${pulseDot} 1.4s ease-in-out infinite;
  }
  .segments {
    display: inline-flex;
    background: var(--surface-elevated);
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 2px;
    gap: 2px;
  }
  .segments button {
    border: 0;
    background: transparent;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: var(--text-xs);
    font-weight: var(--font-weight-medium);
    color: var(--text-secondary);
    cursor: pointer;
    transition:
      background-color 120ms ease,
      color 120ms ease;
  }
  .segments button:hover:not(.active):not([aria-disabled='true']) {
    color: var(--foreground);
  }
  .segments button[aria-disabled='true'] {
    opacity: 0.6;
    cursor: default;
  }
  .segments button.active {
    /* Gold (--accent): a voice/audio selector is a visual/creative control. */
    background: var(--accent);
    color: var(--accent-foreground, #fff);
  }
  .action {
    /* PLAT-11563: the same outline chrome as the header's "Dub with AI Voice"
       button (TranslationToggle's HeaderDubButton) — a secondary control in the
       language menu, not the menu's one green primary action. */
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 3px 10px;
    font-size: var(--text-xs);
    font-weight: var(--font-weight-medium);
    background: transparent;
    color: var(--foreground);
    cursor: pointer;
    transition:
      background-color 120ms ease,
      color 120ms ease;
  }
  .action:hover:not(:disabled) {
    background: var(--surface-elevated);
    color: var(--foreground);
  }
  button:disabled {
    opacity: 0.6;
    cursor: default;
  }
  .note {
    /* Wrap (the dropdown sets nowrap globally) so a long hint never forces the
       control wider than its min-width — width stays constant, height grows. */
    white-space: normal;
    margin: 6px 0 0;
    font-size: var(--text-xs);
    color: var(--text-muted);
  }
  .note.error {
    color: var(--destructive, #c0392b);
  }
`;
